import { useMemo, useEffect, type ChangeEvent, type FocusEvent } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Text } from '@rsos/components/capstone/base';
import InputText from '@rsos/components/capstone/base/form/inputs/InputText';
import { resetState } from '@rsos/reduxDataStore/src/features/geoSelection/geoSelectionSlice';
import type { LocationSelectionMethod } from '../../../../gainsightTrackings/airAmbulanceTrackings';
import type { SetFieldValue, SpatialObject, Coordinates } from '../../types';
import { Label } from '../AirAmbulanceFormStandalone.styles';
import {
  ADDRESS_VALIDATION,
  PICKUP_COORDINATES,
  PICKUP_LOCATION,
  PICKUP_DETAILS,
  LANDING_ZONE_EXTERNAL_ID,
} from '../constants';
import { useLocationEditor, type FormFieldRef } from '../hooks';
import { parseCoordinates } from '../validation';
import AddressSearchInput from './AddressSearchInput';
import LandingZoneSelect from './LandingZoneSelect';

interface FormValues {
  pickup_location: string;
  pickup_coordinates: string;
  pickup_details: string;
}

interface TouchedFields {
  pickup_location?: boolean;
  pickup_coordinates?: boolean;
}

interface GeoSelectionState {
  geoSelection: {
    airAmbulance: {
      coordinates: [number, number] | null;
    };
  };
}

interface LocationSectionProps {
  values: FormValues;
  touched: TouchedFields;
  handleBlur: (e: FocusEvent<HTMLInputElement>) => void;
  handleChange: (e: ChangeEvent<HTMLInputElement>) => void;
  setFieldValue: SetFieldValue;
  formRef: { readonly current: FormFieldRef | null };
  initialCoordinates: string;
  initialAddressValid?: boolean;
  initialLandingZone?: SpatialObject | null;
  reverseGeocodeOnMount?: boolean;
  onLocationSelect?: (
    coords: Coordinates,
    method: LocationSelectionMethod,
  ) => void;
  onAddressValidityChange?: (isValid: boolean) => void;
  requestId?: string;
}

const LocationSection = ({
  values,
  touched,
  handleBlur,
  handleChange,
  setFieldValue,
  formRef,
  initialCoordinates,
  initialAddressValid = true,
  initialLandingZone = null,
  reverseGeocodeOnMount = false,
  onLocationSelect,
  onAddressValidityChange,
  requestId,
}: LocationSectionProps): JSX.Element => {
  const dispatch = useDispatch();
  const airAmbulanceCoordinates = useSelector(
    (state: GeoSelectionState) => state.geoSelection.airAmbulance.coordinates,
  );

  const {
    isReverseGeocoding,
    isAddressValid,
    isAddressSelected,
    geocodedResults,
    reverseGeocodeResults,
    selectedLandingZone,
    setAddressToGeocode,
    setIsAddressValid,
    setIsAddressSelected,
    handleSelectSuggestion,
    handleCoordinateChange,
    handleAddressChange,
    handleLandingZoneSelect,
    handleGeocodingResults,
    triggerReverseGeocode,
  } = useLocationEditor({
    formRef,
    initialCoordinates,
    initialAddressValid,
    initialLandingZone,
    onLocationSelect,
  });

  // Notify parent of address validity changes (used for submit button state)
  useEffect(() => {
    onAddressValidityChange?.(isAddressValid && isAddressSelected);
  }, [isAddressValid, isAddressSelected, onAddressValidityChange]);

  // Trigger reverse geocoding on mount when requested
  useEffect(() => {
    if (reverseGeocodeOnMount) {
      const coords = parseCoordinates(initialCoordinates);
      if (coords) {
        triggerReverseGeocode(coords);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only on mount
  }, []);

  // Forward geocoding: check results when they arrive
  useEffect(() => {
    handleGeocodingResults(values);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when geocoded results change
  }, [geocodedResults]);

  // Map click support
  useEffect(() => {
    if (airAmbulanceCoordinates) {
      const latLng = `${airAmbulanceCoordinates[1]}, ${airAmbulanceCoordinates[0]}`;
      handleCoordinateChange(
        { target: { value: latLng } },
        handleChange,
        'map_click',
      );
      setFieldValue(PICKUP_COORDINATES, latLng);
      dispatch(resetState());
    }
  }, [
    airAmbulanceCoordinates,
    dispatch,
    handleCoordinateChange,
    handleChange,
    setFieldValue,
  ]);

  const COORDINATES_ERROR = useMemo(() => {
    if (touched.pickup_coordinates && !isAddressValid) {
      return ADDRESS_VALIDATION.NO_MATCH;
    }
    if (touched.pickup_coordinates && values.pickup_coordinates.length === 0) {
      return ADDRESS_VALIDATION.REQUIRED;
    }
    return null;
  }, [values.pickup_coordinates, touched.pickup_coordinates, isAddressValid]);

  const GEOCODE_ERROR = useMemo(() => {
    if (
      (values.pickup_location.length > 3 &&
        !geocodedResults?.length &&
        (!isAddressValid || !isAddressSelected)) ||
      (values.pickup_location.length < 4 &&
        values.pickup_location.length > 0 &&
        touched.pickup_location)
    ) {
      return ADDRESS_VALIDATION.NO_MATCH;
    }
    if (touched.pickup_location && values.pickup_location.length === 0) {
      return ADDRESS_VALIDATION.REQUIRED;
    }
    return null;
  }, [
    touched.pickup_location,
    values.pickup_location,
    geocodedResults,
    isAddressValid,
    isAddressSelected,
  ]);

  const handleFieldValue = async (): Promise<void> => {
    await setFieldValue(PICKUP_COORDINATES, '');
    await setFieldValue(PICKUP_LOCATION, '');
    await setFieldValue(LANDING_ZONE_EXTERNAL_ID, '');
    setAddressToGeocode('');
    setIsAddressValid(false);
    setIsAddressSelected(false);
  };

  return (
    <>
      <Box pb={3}>
        <LandingZoneSelect
          setFieldValue={setFieldValue}
          handleChange={handleChange}
          handleLandingZoneSelect={handleLandingZoneSelect}
          selectedLandingZone={selectedLandingZone}
        />
        <AddressSearchInput
          value={values.pickup_location}
          onChange={handleChange}
          onBlur={handleBlur}
          setFieldValue={() => handleFieldValue()}
          error={GEOCODE_ERROR || undefined}
          geocodedResults={reverseGeocodeResults || geocodedResults}
          onSelectSuggestion={handleSelectSuggestion}
          onAddressChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (selectedLandingZone) {
              setFieldValue(LANDING_ZONE_EXTERNAL_ID, '');
            }
            handleAddressChange(e, handleChange);
          }}
          requestId={requestId}
        />
        <Label>
          <Text size="small" color="primaryText">
            Pickup Lat/Long*
          </Text>
        </Label>
        <InputText
          id={PICKUP_COORDINATES}
          name={PICKUP_COORDINATES}
          type="text"
          shouldErrorTakeSpace
          setFieldValue={() => handleFieldValue()}
          value={values.pickup_coordinates}
          onChange={(e: ChangeEvent<HTMLInputElement>) => {
            if (selectedLandingZone) {
              setFieldValue(LANDING_ZONE_EXTERNAL_ID, '');
            }
            handleCoordinateChange(e, handleChange);
          }}
          onBlur={handleBlur}
          error={COORDINATES_ERROR}
          style={{ fontSize: '14px' }}
        />
        {isReverseGeocoding && (
          <Text size="small" color="secondaryText">
            Finding address...
          </Text>
        )}
      </Box>

      <Box pb={3}>
        <Label>
          <Text size="small" color="primaryText">
            Pickup Location Details
          </Text>
        </Label>
        <InputText
          id={PICKUP_DETAILS}
          name={PICKUP_DETAILS}
          type="text"
          setFieldValue={setFieldValue}
          placeholder="Pickup Location Details"
          value={values.pickup_details}
          onChange={handleChange}
          onBlur={handleBlur}
          style={{ fontSize: '14px' }}
        />
      </Box>
    </>
  );
};

export default LocationSection;
